import { useState, useEffect } from "react";
import matter from "gray-matter";
import { Buffer } from "buffer";
import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKPagination from "components/MKPagination";
import SimpleBookingCard from "custom/Cards/BookingCards/SimpleBookingCard";

if (typeof window !== "undefined") {
  window.Buffer = Buffer;
}

function Search() {
  const [searchCriteria, setSearchCriteria] = useState({
    title: "",
    nick: "",
    content: "",
    theme: "",
    author: ""
  });

  const [filteredOptions, setFilteredOptions] = useState({
    titles: [],
    nicks: [],
    themes: [],
    authors: []
  });

  const [filteredPosts, setFilteredPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const repoOwner = "pwncat-offsec";
        const repoName = "Blog";
        const branch = "main";
        const apiUrl = `https://api.github.com/repos/${repoOwner}/${repoName}/contents?ref=${branch}`;

        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`GitHub API request failed: ${response.statusText}`);
        }

        const files = await response.json();
        if (!Array.isArray(files)) {
          throw new Error("GitHub API response is not an array");
        }

        const postsData = await Promise.all(
          files.map(async (file) => {
            if (file.type === "file" && file.name.endsWith(".md")) {
              const res = await fetch(file.download_url);
              const markdownWithMeta = await res.text();
              const { data: frontMatter, content } = matter(markdownWithMeta);
              return {
                frontMatter,
                content,
                postId: file.name.replace('.md', '')
              };
            }
            return null;
          })
        );

        const validPosts = postsData.filter(post => post !== null);

        const filteredPosts = validPosts.filter(post =>
          (searchCriteria.title ? post.frontMatter.title.toLowerCase().includes(searchCriteria.title.toLowerCase()) : true) &&
          (searchCriteria.nick ? post.frontMatter.nick.toLowerCase().includes(searchCriteria.nick.toLowerCase()) : true) &&
          (searchCriteria.content ? post.content.toLowerCase().includes(searchCriteria.content.toLowerCase()) : true) &&
          (searchCriteria.theme ? post.frontMatter.theme.includes(searchCriteria.theme) : true) &&
          (searchCriteria.author ? post.frontMatter.author.toLowerCase().includes(searchCriteria.author.toLowerCase()) : true)
        );

        setFilteredOptions({
          titles: Array.from(new Set(validPosts.map(post => post.frontMatter.title))),
          nicks: Array.from(new Set(validPosts.map(post => post.frontMatter.nick))),
          themes: Array.from(new Set(validPosts.flatMap(post => post.frontMatter.theme))),
          authors: Array.from(new Set(validPosts.map(post => post.frontMatter.author)))
        });

        setFilteredPosts(filteredPosts);
      } catch (error) {
        console.error("Failed to load posts:", error);
      }
    };

    loadPosts();
  }, [searchCriteria]);

  useEffect(() => {
    console.log("Filtered Posts:", filteredPosts);
  }, [filteredPosts]);

  const handleSearchChange = (field) => (event, value) => {
    setSearchCriteria(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const maxDescriptionLength = Math.max(...filteredPosts.map(post => post.content.length));

  const paddedPosts = filteredPosts.map(post => ({
    ...post,
    content: post.content.padEnd(maxDescriptionLength, '\u00A0')
  }));

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = paddedPosts.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <MKBox component="section">
      <Container>
        <Grid container spacing={{ xs: 0, lg: 3 }} sx={{ pt: 2, pb: 3, px: 2, mx: "auto" }}>
          <Grid item xs={12} lg={4} sx={{ mt: 2 }}>
            <MKTypography display="block" variant="button" fontWeight="regular" color="text" mb={1}>
              Título
            </MKTypography>
            <Autocomplete
              options={filteredOptions.titles}
              onChange={handleSearchChange("title")}
              renderInput={(params) => <MKInput {...params} variant="standard" placeholder="Selecciona un título" />}
            />
          </Grid>
          <Grid item xs={12} lg={4} sx={{ mt: 2 }}>
            <MKTypography display="block" variant="button" fontWeight="regular" color="text" mb={1}>
              Nick del Autor
            </MKTypography>
            <Autocomplete
              options={filteredOptions.nicks}
              onChange={handleSearchChange("nick")}
              renderInput={(params) => <MKInput {...params} variant="standard" placeholder="Selecciona un nick" />}
            />
          </Grid>
          <Grid item xs={12} lg={4} sx={{ mt: 2 }}>
            <MKTypography display="block" variant="button" fontWeight="regular" color="text" mb={1}>
              Tema
            </MKTypography>
            <Autocomplete
              options={filteredOptions.themes}
              onChange={handleSearchChange("theme")}
              renderInput={(params) => <MKInput {...params} variant="standard" placeholder="Selecciona un tema" />}
            />
          </Grid>
          <Grid item xs={12} lg={4} sx={{ mt: 2 }}>
            <MKTypography display="block" variant="button" fontWeight="regular" color="text" mb={1}>
              Autor
            </MKTypography>
            <Autocomplete
              options={filteredOptions.authors}
              onChange={handleSearchChange("author")}
              renderInput={(params) => <MKInput {...params} variant="standard" placeholder="Selecciona un autor" />}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {currentPosts.map((post, index) => {
            const postId = post.postId;
            return (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <MKBox mt={3} sx={{ height: '100%' }}>
                  <SimpleBookingCard
                    title={post.frontMatter.title}
                    description={post.frontMatter.description}
                    categories={[post.frontMatter.nick, post.frontMatter.date, post.frontMatter.theme.join(' - ')]}
                    action={{
                      type: "internal",
                      route: `/post/${postId}`,
                      color: "info",
                      label: "Leer Post",
                      component: Link,
                      to: {
                        pathname: `/post/${postId}`,
                        state: { ...post.frontMatter, content: post.content }
                      }
                    }}
                  />
                </MKBox>
              </Grid>
            );
          })}
        </Grid>
        <MKBox mt={5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <MKPagination>
            <MKPagination item onClick={(e) => handlePageChange(e, currentPage - 1)} disabled={currentPage === 1}>
              <Icon>keyboard_arrow_left</Icon>
            </MKPagination>
            {Array.from({ length: Math.ceil(filteredPosts.length / postsPerPage) }, (_, index) => (
              <MKPagination item key={index + 1} active={index + 1 === currentPage} onClick={(e) => handlePageChange(e, index + 1)}>
                {index + 1}
              </MKPagination>
            ))}
            <MKPagination item onClick={(e) => handlePageChange(e, currentPage + 1)} disabled={currentPage === Math.ceil(filteredPosts.length / postsPerPage)}>
              <Icon>keyboard_arrow_right</Icon>
            </MKPagination>
          </MKPagination>
        </MKBox>
      </Container>
    </MKBox>
  );
}

export default Search;